import React, { useState } from 'react';
import { Modal, Form, Input, Select, DatePicker, TimePicker, Radio, Button, message, Divider, Typography, Space, InputNumber } from 'antd';
import { useCart } from '../../contexts/CartContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

function BookingModal({ visible, onCancel, onSuccess }) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [bookingType, setBookingType] = useState('enquiry');
    
    const { cartItems, getCartTotal, getCartItemsByVendor, clearCart } = useCart();
    const cartVendors = getCartItemsByVendor();

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const bookingData = {
                type: bookingType,
                customer: {
                    name: values.name,
                    email: values.email,
                    phone: values.phone,
                },
                event: {
                    date: values.date.format('YYYY-MM-DD'),
                    time: values.time.format('HH:mm'),
                    occasion: values.occasion,
                    venue: values.venue,
                    special_instructions: values.special_instructions,
                },
                guests: {
                    veg: values.guests_veg || 0,
                    non_veg: values.guests_non_veg || 0,
                },
                items: cartItems.map(item => ({
                    menu_id: item.id,
                    quantity: item.quantity,
                    customizations: item.customizations,
                    price: item.price,
                })),
                vendors: cartVendors.map(vendor => ({
                    vendor_id: vendor.vendor_id,
                    items: vendor.items.map(item => ({
                        menu_id: item.id,
                        quantity: item.quantity,
                        customizations: item.customizations,
                        price: item.price,
                    }))
                })),
                total_amount: getCartTotal(),
                status: bookingType === 'enquiry' ? 'pending' : 'confirmed',
            };

            // Here you would make an API call to save the booking
            console.log('Booking data:', bookingData);
            
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            message.success(
                bookingType === 'enquiry' 
                    ? 'Enquiry submitted successfully! We will contact you soon.' 
                    : 'Booking confirmed! Payment details will be sent to your email.'
            );
            
            clearCart();
            form.resetFields();
            onSuccess();
        } catch (error) {
            message.error('Failed to submit booking. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={
                <div className="text-center">
                    <Title level={3} className="mb-2 text-2xl font-bold bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">
                        Complete Your Booking
                    </Title>
                    <Text type="secondary" className="text-slate-600">Review your order and provide event details</Text>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={800}
            className="booking-modal"
            styles={{
                content: { borderRadius: '20px', padding: '24px' },
                header: { borderBottom: '2px solid rgba(252, 128, 25, 0.1)', padding: '20px 24px' }
            }}
        >
            <div className="space-y-6">
                {/* Booking Type Selection */}
                <div className="text-center">
                    <Radio.Group 
                        value={bookingType} 
                        onChange={(e) => setBookingType(e.target.value)}
                        className="booking-type-selector"
                    >
                        <Radio.Button value="enquiry" className="px-8 py-3 h-12 text-base font-semibold rounded-lg">
                            📋 Request Quote (Enquiry)
                        </Radio.Button>
                        <Radio.Button value="payment" className="px-8 py-3 h-12 text-base font-semibold rounded-lg">
                            💳 Book & Pay Now
                        </Radio.Button>
                    </Radio.Group>
                </div>

                <Divider />

                {/* Cart Summary - Modern Design */}
                <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-6 border-2 border-orange-100 shadow-md">
                    <Title level={5} className="mb-4 font-bold text-gray-900">Order Summary</Title>
                    {cartVendors.map((vendor, index) => (
                        <div key={vendor.vendor_id} className="mb-4 pb-4 border-b border-orange-100 last:border-b-0">
                            <Text strong className="text-orange-600 text-base font-bold">{vendor.vendor_name}</Text>
                            <div className="mt-2 space-y-2">
                                {vendor.items.map((item, itemIndex) => (
                                    <div key={itemIndex} className="flex justify-between items-center text-sm bg-white/50 p-2 rounded-lg">
                                        <span className="font-medium text-gray-700">{item.name} x {item.quantity}</span>
                                        <span className="font-semibold text-gray-900">₹{(item.price * item.quantity).toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                    <Divider className="my-4 border-orange-200" />
                    <div className="flex justify-between items-center text-xl font-bold bg-gradient-to-r from-orange-50 to-transparent p-4 rounded-lg">
                        <span className="text-gray-800">Total Amount:</span>
                        <span className="price-modern text-2xl">₹{getCartTotal().toLocaleString()}</span>
                    </div>
                </div>

                {/* Booking Form */}
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    className="booking-form"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            name="name"
                            label={<span className="font-semibold text-gray-700">Full Name</span>}
                            rules={[{ required: true, message: 'Please enter your name' }]}
                        >
                            <Input size="large" placeholder="Enter your full name" className="input-modern-vibrant h-12 rounded-xl" />
                        </Form.Item>
                        <Form.Item
                            name="email"
                            label={<span className="font-semibold text-gray-700">Email Address</span>}
                            rules={[
                                { required: true, message: 'Please enter your email' },
                                { type: 'email', message: 'Please enter a valid email' }
                            ]}
                        >
                            <Input size="large" placeholder="Enter your email" className="input-modern-vibrant h-12 rounded-xl" />
                        </Form.Item>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            name="phone"
                            label={<span className="font-semibold text-gray-700">Phone Number</span>}
                            rules={[{ required: true, message: 'Please enter your phone number' }]}
                        >
                            <Input size="large" placeholder="Enter your phone number" className="input-modern-vibrant h-12 rounded-xl" />
                        </Form.Item>
                        <Form.Item
                            name="occasion"
                            label={<span className="font-semibold text-gray-700">Occasion</span>}
                            rules={[{ required: true, message: 'Please select occasion' }]}
                        >
                            <Select size="large" placeholder="Select occasion" className="h-12 rounded-xl" style={{ borderRadius: '12px' }}>
                                <Select.Option value="wedding">Wedding</Select.Option>
                                <Select.Option value="birthday">Birthday</Select.Option>
                                <Select.Option value="corporate">Corporate Event</Select.Option>
                                <Select.Option value="anniversary">Anniversary</Select.Option>
                                <Select.Option value="other">Other</Select.Option>
                            </Select>
                        </Form.Item>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            name="date"
                            label={<span className="font-semibold text-gray-700">Event Date</span>}
                            rules={[{ required: true, message: 'Please select event date' }]}
                        >
                            <DatePicker 
                                size="large" 
                                className="w-full h-12 rounded-xl"
                                disabledDate={(current) => current && current < dayjs().startOf('day')}
                                style={{ borderRadius: '12px' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="time"
                            label={<span className="font-semibold text-gray-700">Event Time</span>}
                            rules={[{ required: true, message: 'Please select event time' }]}
                        >
                            <TimePicker 
                                size="large" 
                                className="w-full h-12 rounded-xl"
                                format="HH:mm"
                                style={{ borderRadius: '12px' }}
                            />
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="venue"
                        label={<span className="font-semibold text-gray-700">Event Venue</span>}
                        rules={[{ required: true, message: 'Please enter event venue' }]}
                    >
                        <Input size="large" placeholder="Enter event venue address" className="input-modern-vibrant h-12 rounded-xl" />
                    </Form.Item>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            name="guests_veg"
                            label={<span className="font-semibold text-gray-700">Vegetarian Guests</span>}
                            initialValue={0}
                        >
                            <InputNumber 
                                size="large" 
                                min={0} 
                                className="w-full h-12 rounded-xl"
                                placeholder="Number of veg guests"
                                style={{ borderRadius: '12px' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="guests_non_veg"
                            label={<span className="font-semibold text-gray-700">Non-Vegetarian Guests</span>}
                            initialValue={0}
                        >
                            <InputNumber 
                                size="large" 
                                min={0} 
                                className="w-full h-12 rounded-xl"
                                placeholder="Number of non-veg guests"
                                style={{ borderRadius: '12px' }}
                            />
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="special_instructions"
                        label={<span className="font-semibold text-gray-700">Special Instructions</span>}
                    >
                        <TextArea 
                            rows={3} 
                            placeholder="Any special dietary requirements, preferences, or instructions..."
                            className="rounded-xl input-modern-vibrant"
                        />
                    </Form.Item>

                    <div className="flex justify-end gap-3 pt-4">
                        <Button 
                            onClick={onCancel}
                            size="large"
                            className="px-8 h-12 rounded-xl border-2 border-gray-200 hover:border-orange-300 hover:text-orange-600 font-semibold"
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            loading={loading}
                            size="large"
                            className="btn-vibrant px-8 h-12 rounded-xl font-bold text-base"
                        >
                            {bookingType === 'enquiry' ? 'Submit Enquiry' : 'Book & Pay Now'}
                        </Button>
                    </div>
                </Form>
            </div>
        </Modal>
    );
}

export default BookingModal;
